import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import SeoHead from '../../components/seo/SeoHead';
import {
  ArrowLeft,
  MapPin,
  Phone,
  Clock,
  Navigation,
  Shield,
  Heart,
  Pill,
  Stethoscope,
  CheckCircle,
  Star,
  Sparkles,
  Baby,
  FlaskConical,
  Plane,
  Users,
  ScanLine,
  LineChart,
} from 'lucide-react';

/**
 * Pharmacy of reference — local SEO landing page.
 *
 * Targets 9 high-intent keywords: pharmacie Cocody, pharmacie Angré,
 * pharmacie 7ème tranche, pharmacie de référence, pharmacie bon accueil,
 * pharmacie commande France, produits rares Abidjan, grande pharmacie,
 * meilleure pharmacie. Contains 700+ words of original content, structured
 * FAQ (FAQPage schema → Google position 0), and AggregateRating pulled from
 * the Google Business Profile (5.0 / 19 reviews).
 */

const PHONE_PRIMARY = '+225 07 78 41 52 77';
const PHONE_SECONDARY = '+225 27 22 30 05 72';
const GOOGLE_MAPS_URL =
  'https://www.google.com/maps/place/Pharmacie+Saint+Ange+Des+Amis/data=!4m2!3m1!1s0x0:0x590d810c976b53b7';

const SERVICES = [
  {
    icon: Pill,
    name: 'Médicaments',
    description: "Large gamme de médicaments princeps et génériques, avec ruptures de stock rarissimes grâce à un approvisionnement quotidien.",
  },
  {
    icon: Sparkles,
    name: 'Parapharmacie fournie',
    description: "La parapharmacie la plus fournie d'Angré : dermo-cosmétique, soins visage & corps, hygiène, bébé, maternité, senior.",
  },
  {
    icon: Plane,
    name: 'Commandes France & Suisse',
    description: "Vous cherchez un produit rare ? Nous le commandons directement en France ou en Suisse en moins de 10 jours.",
  },
  {
    icon: Baby,
    name: 'Laits infantiles rares',
    description: "Gallia AR, Novalac Relax, Guigoz HA, Modilac Riz, Nutriben, préparations spéciales allergies et prématurés — stockés en permanence.",
  },
  {
    icon: FlaskConical,
    name: 'Produits FIV & fertilité',
    description: "Traitements pour la procréation médicalement assistée disponibles avec conseil pharmaceutique confidentiel.",
  },
  {
    icon: ScanLine,
    name: 'Conseil dermo-cosmétique par IA',
    description: "Application SADASkinCare : diagnostic de peau par intelligence artificielle et ordonnance cosmétique personnalisée sur place.",
  },
  {
    icon: LineChart,
    name: 'Suivi des paramètres de santé',
    description: "Tension, glycémie, IMC, SpO₂, température — diagramme de suivi et rapport imprimable selon les normes OMS.",
  },
  {
    icon: Stethoscope,
    name: 'Dépistages rapides',
    description: "Paludisme (TDR), fièvre typhoïde, groupe sanguin — résultats en moins de 15 minutes.",
  },
];

const DIFFERENTIATORS = [
  'Hall aéré et spacieux, climatisé toute l\'année',
  'Espace de confidentialité d\'excellent standing',
  'Équipe jeune et dynamique, formée aux dernières innovations',
  'Accueil chaleureux et bienveillant pour tous les âges',
  'Commandes France & Suisse sous 10 jours',
  'Parapharmacie la plus fournie du quartier',
  'Laits infantiles rares disponibles en stock',
  'Produits FIV et fertilité en conseil confidentiel',
  'Conseil dermo-cosmétique par application d\'intelligence artificielle',
  'Suivi santé avec diagramme de progression et rapport imprimable',
];

const FAQ_ITEMS = [
  {
    question: 'Quelle est la meilleure pharmacie à Cocody Angré 7ème tranche ?',
    answer:
      "La Pharmacie Saint Ange Des Amis est régulièrement citée parmi les meilleures pharmacies de Cocody et d'Angré 7ème Tranche : 5,0 étoiles sur Google avec 19 avis. Nous y devons notre réputation à un hall aéré et spacieux, un accueil chaleureux de notre équipe jeune et dynamique, et à une parapharmacie particulièrement fournie.",
  },
  {
    question: 'Comment trouver une pharmacie bien achalandée à Abidjan ?',
    answer:
      "La Pharmacie Saint Ange Des Amis est réputée pour être l'une des pharmacies les mieux achalandées d'Abidjan. Nous tenons en stock permanent les produits courants mais aussi des produits rares : laits infantiles spécialisés (Gallia, Guigoz HA, Modilac AR, Novalac), produits FIV, dermo-cosmétique haut de gamme, et nous commandons directement depuis la France ou la Suisse sous 10 jours pour les références indisponibles sur le marché ivoirien.",
  },
  {
    question: 'Où acheter un produit de pharmacie rare en commande France depuis Abidjan ?',
    answer:
      "La Pharmacie Saint Ange Des Amis propose un service de commande France et Suisse pour les produits rares ou non disponibles en Côte d'Ivoire. Il suffit de nous transmettre le nom du produit (ou l'ordonnance) par WhatsApp au 07 78 41 52 77 ou par email contact-info@phciesada.org. Livraison à la pharmacie sous 10 jours ouvrés en moyenne.",
  },
  {
    question: 'Quelle pharmacie a le meilleur accueil à Angré 7ème Tranche ?',
    answer:
      "Le bon accueil est au cœur de notre engagement. Notre équipe jeune et dynamique est formée à l'écoute et au conseil personnalisé. Nous disposons d'un espace de confidentialité d'excellent standing pour les consultations sensibles (dermatologie, contraception, fertilité, problèmes intimes), et notre hall est aéré et spacieux pour un confort optimal.",
  },
  {
    question: 'Proposez-vous un conseil dermo-cosmétique personnalisé ?',
    answer:
      "Oui, notre pharmacie est équipée d'une application de diagnostic de peau par intelligence artificielle (SADASkinCare) : nos pharmaciens formés analysent votre peau et vous proposent une routine cosmétique personnalisée selon votre phototype et vos préoccupations (acné, hyperpigmentation, vieillissement, sensibilité). Service gratuit sur place ou depuis notre application.",
  },
  {
    question: 'Peut-on faire suivre ses paramètres de santé à la Pharmacie Saint Ange Des Amis ?',
    answer:
      "Absolument. Nous proposons un suivi officinal de vos paramètres vitaux (tension artérielle, fréquence cardiaque, glycémie capillaire, IMC, saturation en oxygène, température) avec diagramme de progression visuel et rapport PDF imprimable conforme aux références OMS. Idéal pour le suivi de l'hypertension, du diabète, ou simplement d'un check-up régulier.",
  },
  {
    question: 'Quels sont les horaires de la Pharmacie Saint Ange Des Amis ?',
    answer:
      "Du lundi au vendredi de 7h30 à 20h00, le samedi de 7h30 à 13h00. Dimanche fermé (sauf en semaine de garde). Nous participons régulièrement au tableau des pharmacies de garde d'Abidjan, avec un accueil 24h/24 pendant notre semaine de garde.",
  },
  {
    question: 'Comment aller à la Pharmacie Saint Ange Des Amis ?',
    answer:
      "Nous sommes situés à Angré 7ème Tranche, Carrefour Bluetooth, dans l'enceinte de l'ancien Bar Bluetooth, à Cocody Abidjan. Code Plus : 92V7+H8 Abidjan. Téléphone : 07 78 41 52 77. Itinéraire Google Maps disponible sur notre site phciesada.org.",
  },
];

const PharmacyLocalSeoPage = ({ onBack, onNavigate }) => {
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        mainEntity: FAQ_ITEMS.map((f) => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://phciesada.org/' },
          { '@type': 'ListItem', position: 2, name: 'Pharmacie Cocody Angré', item: 'https://phciesada.org/pharmacie-cocody-angre' },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF8F3] to-white">
      <SeoHead
        title="Pharmacie de référence à Cocody Angré 7ème Tranche — Accueil chaleureux, produits rares"
        description="Pharmacie Saint Ange Des Amis : la pharmacie de référence à Cocody Angré 7ème Tranche. Hall aéré, équipe jeune, accueil chaleureux, commandes France & Suisse, laits infantiles rares, produits FIV, conseil dermo-cosmétique par IA. Ouvert 7h30-20h."
        canonical="/pharmacie-cocody-angre"
        structuredData={structuredData}
      />

      {/* Back button */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4 py-3">
          <Button variant="ghost" onClick={onBack} className="text-[#5D4E37]">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
        </div>
      </div>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 py-10 md:py-16">
        <Badge className="bg-amber-100 text-amber-800 border-amber-300 mb-4">
          Pharmacie de référence à Cocody
        </Badge>
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#3D2E1F] mb-4 leading-tight">
          La pharmacie <span className="text-[#C45C26]">de référence</span> à Cocody, Angré 7<sup>ème</sup> Tranche
        </h1>
        <p className="text-lg text-[#5D4E37] max-w-3xl leading-relaxed">
          À la <strong>Pharmacie Saint Ange Des Amis</strong>, nous avons bâti notre réputation
          sur trois piliers : un <strong>accueil chaleureux</strong> par une équipe jeune et
          dynamique, une <strong>parapharmacie particulièrement fournie</strong> — notamment
          en produits rares commandés depuis la France et la Suisse — et un
          <strong> hall aéré et spacieux</strong> incluant un espace de confidentialité
          d'excellent standing. Recommandée par des centaines de clients de Cocody, Angré,
          7<sup>ème</sup> Tranche, Deux Plateaux, de la Riviera et de toute la région d'Abidjan.
        </p>

        {/* Trust strip */}
        <div className="flex flex-wrap gap-3 mt-6">
          <Badge className="bg-green-100 text-green-800 border-green-300">
            <Star className="w-3.5 h-3.5 mr-1 fill-current" /> 5,0 sur Google (19 avis)
          </Badge>
          <Badge className="bg-blue-100 text-blue-800 border-blue-300">
            <Users className="w-3.5 h-3.5 mr-1" /> Équipe jeune & dynamique
          </Badge>
          <Badge className="bg-purple-100 text-purple-800 border-purple-300">
            <Plane className="w-3.5 h-3.5 mr-1" /> Commandes France & Suisse
          </Badge>
          <Badge className="bg-pink-100 text-pink-800 border-pink-300">
            <Baby className="w-3.5 h-3.5 mr-1" /> Laits infantiles rares
          </Badge>
        </div>

        {/* CTAs */}
        <div className="flex flex-wrap gap-3 mt-8">
          <Button asChild size="lg" className="bg-[#C45C26] hover:bg-[#A64C1F] text-white">
            <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`}>
              <Phone className="w-4 h-4 mr-2" /> Appeler la pharmacie
            </a>
          </Button>
          <Button asChild size="lg" variant="outline" className="border-[#C45C26] text-[#C45C26]">
            <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer">
              <Navigation className="w-4 h-4 mr-2" /> Itinéraire Google Maps
            </a>
          </Button>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-2">
          Une grande pharmacie, 8 services différenciants
        </h2>
        <p className="text-[#5D4E37] mb-8 max-w-3xl">
          Que vous cherchiez un produit rare en commande France, un lait infantile spécialisé,
          un conseil dermo-cosmétique personnalisé ou simplement un bon accueil en pharmacie à
          Angré, vous trouverez chez nous une équipe formée et des stocks à jour en permanence.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <Card key={i} className="border-amber-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg bg-[#C45C26]/10 flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#C45C26]" />
                  </div>
                  <h3 className="font-semibold text-[#3D2E1F] mb-1">{s.name}</h3>
                  <p className="text-sm text-[#5D4E37] leading-relaxed">{s.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Why us — richer content block for SEO */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="bg-white rounded-2xl p-6 md:p-10 shadow-sm border border-amber-100">
          <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-4">
            Pourquoi nous sommes la pharmacie de référence à Angré et à Cocody
          </h2>
          <div className="grid md:grid-cols-2 gap-8 text-[#5D4E37]">
            <div className="space-y-3 leading-relaxed">
              <p>
                Située en plein cœur d'<strong>Angré 7<sup>ème</sup> Tranche</strong>, au Carrefour
                Bluetooth, dans l'enceinte de l'ancien Bar Bluetooth, la{' '}
                <strong>Pharmacie Saint Ange Des Amis</strong> dessert un bassin de clientèle
                fidèle venu de toute la commune de <strong>Cocody</strong> : Angré, 7<sup>ème</sup> Tranche,
                Deux Plateaux, Riviera, Palmeraie, Angré Château, et même des clients venus de plus
                loin qui font le déplacement pour nos produits rares.
              </p>
              <p>
                Ce qui nous distingue des autres pharmacies d'Abidjan ? D'abord, un{' '}
                <strong>hall aéré et spacieux</strong>, climatisé toute l'année, qui garantit un
                confort appréciable même aux heures d'affluence. Ensuite, un{' '}
                <strong>espace de confidentialité d'excellent standing</strong> pour les
                consultations sensibles (dermatologie, fertilité, contraception, problèmes
                intimes) — une rareté à Abidjan.
              </p>
              <p>
                Notre <strong>équipe jeune et dynamique</strong>, constamment formée aux nouvelles
                innovations, est reconnue pour son <strong>accueil chaleureux</strong> et son
                conseil pharmaceutique approfondi. Plus de 19 avis Google 5 étoiles témoignent de
                cette exigence.
              </p>
            </div>
            <div className="space-y-3 leading-relaxed">
              <p>
                Côté produits, nous sommes probablement <strong>la pharmacie la mieux
                achalandée d'Angré</strong>. Notre rayon dermo-cosmétique rivalise avec celui des
                plus grandes parapharmacies de la capitale. Nous stockons en permanence des{' '}
                <strong>laits infantiles spécialisés rares</strong> (anti-régurgitations,
                hypoallergéniques, épaissis, prématurés) et des <strong>produits FIV</strong> avec
                conseil pharmaceutique dédié — deux catégories souvent absentes des pharmacies de
                quartier.
              </p>
              <p>
                Pour les produits introuvables sur le marché ivoirien, nous proposons un service
                de <strong>commande directe depuis la France et la Suisse</strong> : livraison à
                la pharmacie sous 10 jours ouvrés en moyenne. Un service très apprécié de notre
                clientèle expatriée et des familles suivant un traitement spécifique.
              </p>
              <p>
                Enfin, nous sommes la seule pharmacie de la zone à proposer un{' '}
                <strong>conseil dermo-cosmétique par application d'intelligence artificielle</strong>{' '}
                (SADASkinCare) et un <strong>suivi personnalisé de vos paramètres de santé</strong>{' '}
                (tension, glycémie, IMC, SpO₂) avec diagramme de progression et rapport PDF
                imprimable aux normes OMS.
              </p>
            </div>
          </div>

          {/* Differentiators checklist */}
          <div className="grid sm:grid-cols-2 gap-2 mt-8 pt-6 border-t border-amber-100">
            {DIFFERENTIATORS.map((d, i) => (
              <div key={i} className="flex items-start gap-2 text-sm text-[#3D2E1F]">
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                <span>{d}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-4xl mx-auto px-4 py-10" id="faq">
        <h2 className="text-2xl md:text-3xl font-bold text-[#3D2E1F] mb-6">Questions fréquentes</h2>
        <div className="space-y-3">
          {FAQ_ITEMS.map((item, i) => (
            <details
              key={i}
              className="bg-white rounded-xl border border-amber-100 overflow-hidden group"
            >
              <summary className="cursor-pointer p-4 font-semibold text-[#3D2E1F] flex items-center justify-between hover:bg-amber-50/50 transition-colors">
                <span>{item.question}</span>
                <span className="text-[#C45C26] group-open:rotate-180 transition-transform">▾</span>
              </summary>
              <div className="px-4 pb-4 text-[#5D4E37] leading-relaxed border-t border-amber-100 pt-3">
                {item.answer}
              </div>
            </details>
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="max-w-6xl mx-auto px-4 py-10">
        <div className="bg-gradient-to-r from-[#C45C26] to-[#A64C1F] rounded-2xl p-6 md:p-10 text-white">
          <div className="flex flex-wrap justify-between items-center gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Passez nous voir aujourd'hui</h2>
              <p className="text-white/90 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Angré 7<sup>ème</sup> Tranche, Carrefour Bluetooth — Cocody, Abidjan
              </p>
              <p className="text-white/90 flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4" />
                Lun–Ven 7h30 – 20h00 • Sam 7h30 – 13h00
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button asChild size="lg" className="bg-white text-[#C45C26] hover:bg-amber-50">
                <a href={`tel:${PHONE_PRIMARY.replace(/\s/g, '')}`}>
                  <Phone className="w-4 h-4 mr-2" /> {PHONE_PRIMARY}
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-white text-white hover:bg-white/10">
                <a href={`tel:${PHONE_SECONDARY.replace(/\s/g, '')}`}>
                  <Phone className="w-4 h-4 mr-2" /> {PHONE_SECONDARY}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PharmacyLocalSeoPage;
